import React, { useEffect, useMemo, useState } from "react";
import { apiJson } from "../../auth/authClient";

const API = "/api/udrzba";

function StatCard({ title, value, sub }) {
  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 16,
        padding: 16,
        background: "#fff",
      }}
    >
      <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 28, fontWeight: 800 }}>{value}</div>
      {sub ? <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>{sub}</div> : null}
    </div>
  );
}

function money(v) {
  return `${Number(v || 0).toLocaleString("cs-CZ")} Kč`;
}

function iso(d) {
  return new Date(d).toISOString().slice(0, 10);
}

function periodicitaLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "mesicne") return "Měsíčně";
  if (x === "ctvrtletne") return "Čtvrtletně";
  if (x === "pulrocne") return "Půlročně";
  if (x === "rocne") return "Ročně";
  if (x === "dle_domluvy") return "Dle domluvy";
  return v || "-";
}

function typPeceLabel(v) {
  const x = String(v || "").trim().toLowerCase();
  if (x === "jednorazova") return "Jednorázová";
  if (x === "predplatne") return "Předplatné";
  if (x === "balicek" || x === "balicek_4x") return "Balíček";
  return v || "-";
}

function nextStepLabel(x) {
  const stav = String(x?.stav || "").trim();
  const stavPredplatneho = String(x?.stavPredplatneho || "").trim();
  const financniStav = String(x?.financniStav || "").trim();

  if (stavPredplatneho === "ceka_na_realizaci") return "Zaevidovat platbu obnovy";
  if (stavPredplatneho === "ceka_na_obnovu") return "Připravit obnovu";
  if (financniStav === "zaplaceno" && stav !== "vyfakturovano") return "Vyfakturovat";
  if (stav === "dokonceno" && financniStav !== "zaplaceno") return "Zaevidovat platbu";
  return "";
}

function badgeStyle(status) {
  const base = {
    display: "inline-block",
    padding: "4px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };

  if (status === "aktivni") {
    return { ...base, background: "#ecfdf5", color: "#065f46", borderColor: "#a7f3d0" };
  }
  if (status === "konci_brzy") {
    return { ...base, background: "#fffbeb", color: "#92400e", borderColor: "#fde68a" };
  }
  if (status === "ceka_na_obnovu") {
    return { ...base, background: "#fef2f2", color: "#991b1b", borderColor: "#fecaca" };
  }
  if (status === "vyprselo") {
    return { ...base, background: "#fff1f2", color: "#9f1239", borderColor: "#fbcfe8" };
  }
  return { ...base, background: "#f3f4f6", color: "#374151", borderColor: "#d1d5db" };
}

function statusLabel(status) {
  if (status === "aktivni") return "Aktivní";
  if (status === "konci_brzy") return "Končí brzy";
  if (status === "ceka_na_obnovu") return "Čeká na obnovu";
  if (status === "vyprselo") return "Vypršelo";
  return "-";
}

export default function UdrzbaDashboard() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    apiJson(`${API}/zakazky`, { method: "GET" })
      .then((d) => setItems(Array.isArray(d?.items) ? d.items : []))
      .catch(() => setItems([]));
  }, []);

  const today = iso(new Date());
  const tomorrowDate = new Date();
  tomorrowDate.setDate(tomorrowDate.getDate() + 1);
  const tomorrow = iso(tomorrowDate);

  const stats = useMemo(() => {
    const all = items.length;
    const nove = items.filter((x) => x.stav === "nova").length;
    const dokoncene = items.filter((x) => x.stav === "dokonceno").length;
    const nezaplacene = items.filter((x) => !x.zaplaceno).length;
    const zaplacene = items.filter((x) => !!x.zaplaceno).length;
    const dnes = items.filter((x) => (x.planovaneDatum || x.datumZhotoveni || "") === today).length;
    const zitra = items.filter((x) => (x.planovaneDatum || x.datumZhotoveni || "") === tomorrow).length;

    const aktivni = items.filter((x) => x.stavPredplatneho === "aktivni").length;
    const konciBrzy = items.filter((x) => x.stavPredplatneho === "konci_brzy").length;
    const cekaNaObnovu = items.filter((x) => x.stavPredplatneho === "ceka_na_obnovu").length;
    const vyprselo = items.filter((x) => x.stavPredplatneho === "vyprselo").length;

    const sumaNezaplacena = items
      .filter((x) => !x.zaplaceno)
      .reduce((sum, x) => sum + Number(x?.soucty?.sDph || 0), 0);

    return {
      all,
      nove,
      dokoncene,
      nezaplacene,
      zaplacene,
      dnes,
      zitra,
      aktivni,
      konciBrzy,
      cekaNaObnovu,
      vyprselo,
      sumaNezaplacena,
    };
  }, [items, today, tomorrow]);

  const upcoming = useMemo(() => {
    return [...items]
      .filter((x) => !!x.dalsiNavsteva)
      .sort((a, b) => String(a.dalsiNavsteva || "").localeCompare(String(b.dalsiNavsteva || "")))
      .slice(0, 8);
  }, [items]);

  const renewal = useMemo(() => {
    return [...items]
      .filter((x) => ["konci_brzy", "ceka_na_obnovu", "vyprselo"].includes(String(x.stavPredplatneho || "")))
      .sort((a, b) => {
        const sa = String(a.stavPredplatneho || "");
        const sb = String(b.stavPredplatneho || "");
        return sa.localeCompare(sb);
      })
      .slice(0, 8);
  }, [items]);

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Dashboard</h2>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 18,
        }}
      >
        <StatCard title="Všechny zakázky" value={stats.all} />
        <StatCard title="Nové" value={stats.nove} />
        <StatCard title="Dokončeno" value={stats.dokoncene} />
        <StatCard title="Nezaplacené" value={stats.nezaplacene} sub={money(stats.sumaNezaplacena)} />
        <StatCard title="Zaplacené" value={stats.zaplacene} />
        <StatCard title="Dnes" value={stats.dnes} />
        <StatCard title="Zítra" value={stats.zitra} />
        <StatCard title="Aktivní péče" value={stats.aktivni} />
        <StatCard title="Končí brzy" value={stats.konciBrzy} />
        <StatCard title="Čeká na obnovu" value={stats.cekaNaObnovu} />
        <StatCard title="Vypršelo" value={stats.vyprselo} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
        }}
      >
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, background: "#fff", padding: 16 }}>
          <div style={{ fontWeight: 800, marginBottom: 12 }}>Nejbližší další návštěvy</div>
          {!upcoming.length ? (
            <div style={{ color: "#6b7280" }}>Zatím žádné další návštěvy.</div>
          ) : (
            upcoming.map((x) => (
              <div key={x.id} style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ fontWeight: 700 }}>{x.dalsiNavsteva || "-"}</div>
                <div style={{ fontSize: 14 }}>
                  {x.objednavatel || "Bez jména"} — {x.cisloHrobu || "-"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {typPeceLabel(x.typPece)} {x.periodicita ? `• ${periodicitaLabel(x.periodicita)}` : ""}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 16, background: "#fff", padding: 16 }}>
          <div style={{ fontWeight: 800, marginBottom: 12 }}>Zakázky k řešení / obnovení</div>
          {!renewal.length ? (
            <div style={{ color: "#6b7280" }}>Zatím nic k řešení.</div>
          ) : (
            renewal.map((x) => (
              <div key={x.id} style={{ padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 700 }}>{x.objednavatel || "Bez jména"}</div>
                  <span style={badgeStyle(x.stavPredplatneho)}>{statusLabel(x.stavPredplatneho)}</span>
                </div>
                <div style={{ fontSize: 14 }}>
                  {x.cisloHrobu || "-"} • {x.typPece || "-"}
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Další návštěva: {x.dalsiNavsteva || "-"} • Uhrazeno do: {x.uhrazenoDo || "-"} • Zbývá návštěv: {Number(x.zbyvaNavstev || 0)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
